import React, { useEffect, useState } from 'react';
import './RecentUpdates.css';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { getAllRecentUpdates } from '../../../../redux/TaskReducer/action';
import { Pagination } from 'react-bootstrap';
import { ChevronDown, Clock, Filter, Search } from 'react-bootstrap-icons';
import { getTimeAgo } from '../../../../utils/dateTimeUtils';
import { FiUser } from "react-icons/fi";
import { IoDocumentTextOutline, IoFunnel, IoFunnelOutline } from "react-icons/io5";
import DocumentIconSvg from '../../../../components/SVG/DocumentIconSvg';
import FieldSvg from '../../../../components/SVG/FieldSvg';
import useDebounce from '../../../../hooks/useDebounce';

const RecentUpdates = () => {

    const iconMap = {
        FIELD: <FieldSvg fillColor={"var(--secondary)"} strokeWidth={"2"} />,
        DOCUMENT: <DocumentIconSvg fillColor={"var(--secondary)"} />,
    };

    const CATEGORIES = {
        all: "All Categories",
        1: "Document",
        2: "Field",
    };

    const { recentUpdates } = useSelector((store) => {
        return {
            recentUpdates: store.TasksReducer.allRecentUpdates
        }
    });

    const dispatch = useDispatch();
    const navigate = useNavigate();

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const totalPages = recentUpdates?.pagination?.totalPages;
    
    const [open, setOpen] = useState(false);
    const [searchText, setSearchText] = useState("");

    const [recentUpdatesFilters, setRecentUpdatesFilters] = useState({
        customer_name: "",
        readable_task_id: "",
        category_id: "",
        title: "",
        tags: "",
        global_search: "",
    })

    const debouncedSearch = useDebounce(searchText, 500);

    useEffect(() => {
        setCurrentPage(1);
        setRecentUpdatesFilters(prev => ({
            ...prev,
            global_search: debouncedSearch
        }));
    }, [debouncedSearch]);

    useEffect(() => {
        dispatch(getAllRecentUpdates(currentPage, limit, recentUpdatesFilters))
    }, [currentPage, limit, recentUpdatesFilters, dispatch]);

    const getPaginationItems = (currentPage, totalPages) => {
        const items = [];
        const pageRange = 2;

        // Prev
        items.push(
            <Pagination.Prev
                key="prev"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(currentPage - 1)}
            />
        );

        // First page
        items.push(
            <Pagination.Item
                key={1}
                active={currentPage === 1}
                onClick={() => setCurrentPage(1)}
            >
                1
            </Pagination.Item>
        );

        // Left ellipsis
        if (currentPage > pageRange + 2) {
            items.push(<Pagination.Ellipsis key="left-ellipsis" disabled />);
        }

        // Middle pages
        const start = Math.max(2, currentPage - pageRange);
        const end = Math.min(totalPages - 1, currentPage + pageRange);

        for (let i = start; i <= end; i++) {
            items.push(
                <Pagination.Item
                    key={i}
                    active={currentPage === i}
                    onClick={() => setCurrentPage(i)}
                >
                    {i}
                </Pagination.Item>
            );
        }

        // Right ellipsis
        if (currentPage < totalPages - (pageRange + 1)) {
            items.push(<Pagination.Ellipsis key="right-ellipsis" disabled />);
        }

        // Last page
        if (totalPages > 1) {
            items.push(
                <Pagination.Item
                    key={totalPages}
                    active={currentPage === totalPages}
                    onClick={() => setCurrentPage(totalPages)}
                >
                    {totalPages}
                </Pagination.Item>
            );
        }

        // Next
        items.push(
            <Pagination.Next
                key="next"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(currentPage + 1)}
            />
        );

        return items;
    };

    const handleSelect = (key) => {
        setCurrentPage(1);
        setRecentUpdatesFilters(prev => ({
            ...prev,
            category_id: key == "all" ? "" : Number(key)
        }));
        setOpen(false);
    };

    return (
        <div className="recent-updates-container">
            <div className="task-search-wrapper">
                {/* Search Box */}
                <div className="task-search-input">
                    <Search size={18} />
                    <input
                        type="text"
                        placeholder="Search by task ID, title..."
                        // value={recentUpdatesFilters.global_search}
                        value={searchText}
                        onChange={(e) => {
                            // setCurrentPage(1);
                            // setRecentUpdatesFilters(prev => ({
                            //     ...prev,
                            //     global_search: e.target.value
                            // }))

                            setSearchText(e.target.value)
                        }}
                    />
                </div>

                {/* Category Filter */}
                <div className="task-filter-container">
                    <button 
                        className={`task-filter-btn ${open ? "active" : ""}`}
                        onClick={() => setOpen(!open)}
                    >
                        <IoFunnelOutline size={16} />
                        {/* <span>All Categories</span> */}
                        {recentUpdatesFilters.category_id ? CATEGORIES[recentUpdatesFilters.category_id] : "All Categories"}
                        <ChevronDown size={16} />
                    </button>

                    {open && (
                        <div className="task-filter-dropdown">
                            {Object.entries(CATEGORIES).map(([key, label]) => {
                                const isSelected = (key === "all" && (recentUpdatesFilters.category_id === null || recentUpdatesFilters.category_id === "")) || key == recentUpdatesFilters.category_id;

                                return (
                                    <div
                                        key={key}
                                        className={`task-filter-item ${isSelected ? "selected" : ""}`}
                                        onClick={() => {
                                            handleSelect(key);
                                        }}
                                    >
                                        {label}
                                    </div>
                                )
                            })}
                        </div>
                    )}
                </div>
            </div>

            <div className="activity-wrapper">
                {recentUpdates.data?.map((item) => (
                    <div 
                        key={item.history_id} 
                        className={`activity-card ${item.task_category_id}`}
                    >
                        <div className="activity-icon">{iconMap[item.task_category_id == 1 ? "DOCUMENT" : "FIELD"]}</div>

                        <div className="activity-content">
                            {/* <div className="activity-header">
                                <h6>{item.title}</h6>
                                <span className="activity-tag">{item.tag}</span>
                            </div> */}

                            <p className="activity-title">{item.title}</p>

                            <div className="activity-meta">
                                <span className="task-id">
                                    <IoDocumentTextOutline />
                                    {item.readable_task_id}
                                </span>
                                <span className="user">
                                    <FiUser />
                                    {item.customer_name}
                                </span>
                                <span
                                    onClick={() => {
                                        navigate(`/task-management/${item?.task_id}/subtask-verification/${item?.task_category_id == 1 ? "DOCUMENT" : "FIELD"}`)
                                    }}
                                    className="view-task"
                                >
                                    View Task
                                </span>
                            </div>
                        </div>

                        <div className="activity-time">
                            <Clock size={14} />
                            <span>{getTimeAgo(item.updated_at)}</span>
                        </div>
                    </div>
                ))}
            </div>
        
            <div className="pagination-wrapper">
                <Pagination className="custom-pagination">{getPaginationItems(currentPage, totalPages)}</Pagination>
            </div>
        </div>
    )
}

export default RecentUpdates